import { failed, ok } from "./result.js";
/**
 * A refusal in the reader's language when the catalogue has its key, the service's words when not.
 * A param with a dotted catalogue key beside it (`type` with `type_key`) is said in the catalogue's
 * word: a document type a module declared, sent in its English with its key — the same rule a cell
 * follows (`value_key_from`). A key the catalogue lacks leaves the param as it came.
 */
export function problemText(problem, i18n) {
    if (problem.key) {
        const said = i18n.t(problem.key, problem.params ? paramsWorded(problem.params, i18n) : problem.params);
        if (said !== problem.key)
            return said;
    }
    return problem.message;
}
function paramsWorded(params, i18n) {
    const out = { ...params };
    for (const [name, key] of Object.entries(params)) {
        if (!name.endsWith('_key') || typeof key !== 'string' || !key.includes('.'))
            continue;
        const of = name.slice(0, -4);
        if (!(of in params))
            continue;
        const word = i18n.t(key);
        if (word !== key)
            out[of] = word;
    }
    return out;
}
/** Headers a caller may not set: the session's credential and the body's type are the session's to write. */
const RESERVED_HEADERS = new Set(['authorization', 'content-type', 'cookie']);
const WILDCARD = '*';
/**
 * Holds the access token in memory and nowhere else, and the refresh token not at all: that one
 * lives in an httpOnly cookie on identity's own origin, which this code cannot read and a script
 * injected into this page cannot either.
 *
 * The consequence is that a page reload has no token. resume() asks identity for a new one with
 * the cookie, which is also how "am I still signed in" is answered.
 */
export class Session {
    token = null;
    who = null;
    /**
     * The refresh in flight, shared. A page that starts four requests at once gets four 401s, and
     * four refreshes would present the same rotated token twice — which identity treats as a replay
     * and answers by burning the whole family, signing the person out for being quick.
     */
    refreshing = null;
    /**
     * Bumped whenever the session this object represents stops being the one a caller started under
     * — a sign-out, or a refresh that came back as somebody else. Work that began under an older
     * generation is stale: it may not adopt a token, and it may not be retried.
     */
    generation = 0;
    /**
     * Set the moment a sign-out begins and cleared only by a fresh sign-in. While it holds, nothing
     * may fetch a token with the cookie: the person has asked for the session to end, and a grant
     * that arrives afterwards is the session coming back from the dead.
     */
    signedOut = false;
    listeners = new Set();
    options;
    trusted;
    constructor(options) {
        this.options = options;
        this.trusted = destinationPolicy(options.identityUrl, options.trustedOrigins ?? []);
    }
    principal() { return this.who; }
    accessToken() { return this.token; }
    holds(permission) {
        const p = this.who;
        return !!p && (p.permissions.includes(WILDCARD) || p.permissions.includes(permission));
    }
    /** Fires whenever the principal changes, including on sign-out. Returns the unsubscribe. */
    onChange(fn) {
        this.listeners.add(fn);
        return () => { this.listeners.delete(fn); };
    }
    set(principal, token) {
        this.who = principal;
        this.token = token;
        for (const fn of this.listeners)
            fn(principal);
    }
    /**
     * `identifier` is an email address or a mobile number (2026-10-14); identity tells them apart by
     * the `@` and reads a number in any usual form («0501234567», «+966 50 123 4567»).
     */
    async login(identifier, password) {
        const at = this.generation;
        const r = await this.request(this.options.identityUrl, '/auth/login', { method: 'POST', body: { identifier: identifier.trim(), password } }, { credentials: true });
        if (r.state !== 'ok')
            return r;
        // Signing in is how a sign-out ends. Lifting the bar before adopting, and only on the way in.
        if (this.generation === at)
            this.signedOut = false;
        const principal = this.adopt(r.data, at);
        if (!principal)
            return this.stale();
        // A token from login names no workspace and carries no permissions: choosing one comes next —
        // a workspace to work in, or a portal to enter as a party (docs/portals.md).
        return ok({ principal, workspaces: r.data.workspaces ?? [], portals: Array.isArray(r.data.portals) ? r.data.portals : [] });
    }
    /**
     * Exchanges a user-level token for a portal token in one workspace (`POST /auth/select-portal`):
     * the person enters as the party they are there, with that workspace's `portal.*` permissions.
     */
    async selectPortal(tenantId) {
        const at = this.generation;
        const r = await this.request(this.options.identityUrl, '/auth/select-portal', { method: 'POST', body: { tenant_id: tenantId } }, { credentials: true });
        if (r.state !== 'ok')
            return r;
        const principal = this.adopt(r.data, at);
        return principal ? ok(principal) : this.stale();
    }
    /**
     * Exchanges a user-level token for one that names a workspace and carries its permissions. A
     * workspace whose roles require two-step sign-in answers 428 until `proof` carries the code; the
     * refusal comes back as it is, with its reason, for the screen to ask.
     */
    async selectTenant(tenantId, proof = {}) {
        const at = this.generation;
        const body = { tenant_id: tenantId };
        if (proof.code)
            body['code'] = proof.code;
        if (proof.recovery_code)
            body['recovery_code'] = proof.recovery_code;
        const r = await this.request(this.options.identityUrl, '/auth/select-tenant', { method: 'POST', body }, { credentials: true });
        if (r.state !== 'ok')
            return r;
        const principal = this.adopt(r.data, at);
        return principal ? ok(principal) : this.stale();
    }
    /**
     * The cookie is the only thing that survives a reload, so this is both "refresh the token" and
     * "was I signed in". A failure here is a signed-out state, not an error to show.
     */
    async resume() {
        return this.refresh();
    }
    refresh() {
        // The cookie may still work. That is not a reason to use it: the person ended this session.
        if (this.signedOut)
            return Promise.resolve(this.stale());
        if (this.refreshing)
            return this.refreshing;
        // Clearing only its own promise. A sign-out drops the one in flight, and an unconditional
        // clear here would then null a *newer* refresh when the old one finally settles — leaving two
        // running against one cookie, which is the replay this exists to prevent.
        const started = this.doRefresh().finally(() => { if (this.refreshing === started)
            this.refreshing = null; });
        this.refreshing = started;
        return started;
    }
    async doRefresh() {
        const at = this.generation;
        const r = await this.request(this.options.identityUrl, '/auth/refresh', { method: 'POST' }, { credentials: true });
        // Somebody signed out while this was in the air. Adopting the grant now would put a working
        // token back into a session the person has already ended, and no listener would say so.
        if (this.generation !== at || this.signedOut)
            return this.stale();
        if (r.state !== 'ok') {
            // No usable session. Clearing it is what moves the shell to the sign-in screen, rather than
            // leaving a dead token that makes every later call fail one at a time.
            this.set(null, null);
            return r.state === 'rejected' ? failed('forbidden', r.message) : r;
        }
        const principal = this.adopt(r.data, at);
        return principal ? ok(principal) : this.stale();
    }
    /**
     * Takes a grant and remembers it. The subject is read out of the token's own payload without
     * verifying it: the shell has no key and needs none, because nothing it decides here is an
     * authorisation. Every unit verifies the signature before it acts.
     */
    adopt(grant, at) {
        // Every path to a token comes through here, and every one of them took time. If the session
        // moved on while this was in the air — a sign-out, another workspace chosen — the grant is a
        // dead letter. Returning null rather than adopting is what keeps a finished sign-out finished.
        if (this.generation !== at || this.signedOut)
            return null;
        const claims = claimsOf(grant.access_token);
        const principal = {
            sub: typeof claims['sub'] === 'string' ? claims['sub'] : '',
            tenant_id: grant.tenant_id ?? (typeof claims['tenant_id'] === 'string' ? claims['tenant_id'] : null),
            permissions: grant.permissions ?? (Array.isArray(claims['permissions']) ? claims['permissions'] : []),
            expires_at: Math.floor(Date.now() / 1000) + grant.expires_in,
            ...((grant.kind ?? claims['knd']) === 'portal' ? { kind: 'portal' } : {}),
        };
        // A grant naming a different person or workspace is a different session, wherever it came
        // from — a refresh, or another tab's choice arriving through the shared cookie. Work started
        // under the old one must not continue: the retry that follows would carry this token.
        // Nobody-to-somebody counts: that is not the same session either.
        if (!sameContext(this.who, principal))
            this.generation += 1;
        this.set(principal, grant.access_token);
        return principal;
    }
    /** What a caller gets when its grant arrived too late to be anyone's. */
    stale() {
        return failed('forbidden', 'the session changed before this could be used');
    }
    /**
     * Ends the session here first, then asks identity to end it there. The order matters: awaiting
     * the answer before clearing leaves a window in which a refresh already in flight can hand the
     * token back. The result says whether identity confirmed it, because "we could not reach it" and
     * "it is gone" are different answers and only one of them is safe to believe on another device.
     */
    async logout() {
        this.generation += 1;
        this.signedOut = true;
        // The refresh in flight is left where it is on purpose. Dropping the reference would not stop
        // it — it would only let a second one start beside it, two refreshes against one cookie, which
        // identity reads as a replay. It can no longer adopt anything; letting it finish is enough.
        this.set(null, null);
        const r = await this.request(this.options.identityUrl, '/auth/logout', { method: 'POST' }, { credentials: true });
        // Clearing a second time: whatever settled while this was in the air, the session ends here.
        if (this.signedOut)
            this.set(null, null);
        if (r.state === 'ok')
            return ok(null);
        // 401 is identity saying there was no session to end, which is the outcome asked for. Any
        // other refusal — a proxy's 404, a 405, a rate limit — is an answer from something, not
        // evidence that the cookie family is gone.
        if (r.state === 'rejected' && r.status === 401)
            return ok(null);
        return r.state === 'rejected' ? failed('unavailable', r.message) : r;
    }
    /**
     * Reads in flight, by address, so the same read asked twice while the first is on its way is
     * sent once (MGR-13): a deep link drew the header's badge, the hub and the sidebar's conditions in
     * one breath, and each asked the same routes — `/common/calendar/settings` four times. Only a plain
     * read is shared (no signal, header, timeout or file); an answer is never kept once it has come,
     * so a read after a write always asks again. Keyed by the generation, so nothing crosses a change
     * of person or workspace.
     */
    inFlight = new Map();
    /** A read of a unit. Four states, and a 401 costs one refresh and one retry before it is a state. */
    async get(baseUrl, path, opts = {}) {
        const plain = Object.keys(opts).every((k) => opts[k] === undefined || (k === 'method' && opts.method === 'GET'));
        if (!plain)
            return this.read(baseUrl, path, opts);
        const key = `${this.generation}\u0000${baseUrl.replace(/\/$/, '')}${path}`;
        const known = this.inFlight.get(key);
        if (known)
            return known;
        const asked = this.read(baseUrl, path, opts);
        this.inFlight.set(key, asked);
        void asked.finally(() => { if (this.inFlight.get(key) === asked)
            this.inFlight.delete(key); });
        return asked;
    }
    async read(baseUrl, path, opts) {
        const r = await this.call(baseUrl, path, opts);
        // A read has no refusal to report: whatever the service said, the data is not here. Its reason
        // travels with its key, so the notice can say it in the reader's language; its status, so a host
        // that reads a 404 as «none of yours» can tell it from a unit that is down.
        return r.state === 'rejected' ? failed('unavailable', r.message, saidOf(r.problems), r.status) : r;
    }
    /** A write. Carries the service's refusal through, because the person has to see it. */
    async send(baseUrl, path, opts) {
        return this.call(baseUrl, path, opts);
    }
    async call(baseUrl, path, opts) {
        // The registry names this address, and the registry is not something this code can verify.
        // Sending the token somewhere else on its say-so is how a token leaves the site it belongs to.
        if (!this.trusted(baseUrl)) {
            return failed('unavailable', `this unit's address is not one the shell will send a token to: ${baseUrl}`);
        }
        // Nothing to send. A call with no token cannot be "refreshed and retried" into one: whoever
        // signs in next is a different person from the nobody this started as.
        if (!this.token)
            return failed('forbidden', 'not signed in');
        const at = this.generation;
        // The cookie travels only to the address it was set by, and only when the call asks for it.
        const credentials = opts.credentials === true && sameOrigin(baseUrl, this.options.identityUrl);
        const first = await this.request(baseUrl, path, opts, { credentials, token: this.token });
        if (!(first.state === 'rejected' && first.status === 401))
            return first;
        // Signing out between the request and its answer ends this call. Refreshing now would fetch a
        // working token with a cookie the person has just asked to have revoked, and put the session
        // back on its feet after they left.
        if (this.generation !== at)
            return failed('forbidden', 'the session ended');
        const again = await this.refresh();
        if (again.state !== 'ok')
            return failed('forbidden', 'the session has ended');
        // The refresh cookie is shared by every tab on this site. Another tab can have chosen a second
        // workspace since this request began, in which case the token now in hand is a different
        // person or a different workspace — and replaying this body under it would write it there.
        // Read the token in the same breath as the check, and send that one: the field can change
        // under any await, and comparing one token while dispatching another proves nothing.
        if (this.generation !== at)
            return failed('forbidden', 'the session changed while this was in flight');
        const token = this.token;
        const second = await this.request(baseUrl, path, opts, { credentials, token });
        if (second.state === 'rejected' && second.status === 401) {
            // The token this used is dead. Whether the *session* is dead is a different question: one
            // may have been signed into since, and clearing that would end somebody else's.
            if (this.generation === at && this.token === token)
                this.set(null, null);
            return failed('forbidden', 'the session has ended');
        }
        return second;
    }
    async request(baseUrl, path, opts, how) {
        const timeoutMs = opts.timeoutMs ?? this.options.timeoutMs ?? 10_000;
        const headers = {};
        for (const [name, value] of Object.entries(opts.headers ?? {})) {
            if (!RESERVED_HEADERS.has(name.toLowerCase()))
                headers[name] = value;
        }
        if (opts.body !== undefined)
            headers['content-type'] = 'application/json';
        if (how.token)
            headers['authorization'] = `Bearer ${how.token}`;
        const timeout = AbortSignal.timeout(timeoutMs);
        const signal = opts.signal ? anySignal([opts.signal, timeout]) : timeout;
        let res;
        try {
            res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
                method: opts.method ?? 'GET',
                headers,
                ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
                ...(how.credentials ? { credentials: 'include' } : {}),
                signal,
            });
        }
        catch (e) {
            const name = e.name;
            if (name === 'TimeoutError' || name === 'AbortError')
                return failed('timeout', `no answer in ${timeoutMs}ms`);
            // A browser reports a blocked cross-origin request as a network failure with no detail, so
            // this is also what a missing CORS header looks like from here.
            return failed('unavailable', e.message || 'the unit could not be reached');
        }
        if (res.status >= 500)
            return failed('unavailable', await reasonOf(res));
        const type = res.headers?.get?.('content-type') ?? '';
        if (opts.as === 'file' && res.ok && !/json/i.test(type)) {
            try {
                const blob = await res.blob();
                return ok({ blob, filename: filenameOf(res.headers?.get?.('content-disposition') ?? null), type: type || blob.type });
            }
            catch (e) {
                const name = e.name;
                if (name === 'TimeoutError' || name === 'AbortError')
                    return failed('timeout', `no answer in ${timeoutMs}ms`);
                return failed('unavailable', e.message || 'the answer was cut off');
            }
        }
        let text;
        try {
            // Headers arriving is not the answer arriving. A connection that dies between them rejects
            // here, and outside a boundary that throw leaves a screen loading with nothing to read.
            text = await res.text();
        }
        catch (e) {
            const name = e.name;
            if (name === 'TimeoutError' || name === 'AbortError')
                return failed('timeout', `no answer in ${timeoutMs}ms`);
            return failed('unavailable', e.message || 'the answer was cut off');
        }
        const body = text ? safeJson(text) : undefined;
        if (res.ok)
            return ok(body);
        if (res.status === 403)
            return failed('forbidden', messageOf(body) ?? 'not allowed', saidOf(problemsOf(body)));
        const confirm = confirmOf(body);
        return { state: 'rejected', status: res.status, message: messageOf(body) ?? `HTTP ${res.status}`, ...(problemsOf(body) ? { problems: problemsOf(body) } : {}), ...(confirm ? { confirm } : {}) };
    }
}
/** Whether a token still belongs to the person and workspace a request was started under. */
function sameContext(before, after) {
    // No principal is not "any principal". Going from nobody to somebody is a change of session as
    // surely as going from one person to another, and work begun as nobody must not continue as them.
    if (!before)
        return false;
    return before.sub === after.sub && before.tenant_id === after.tenant_id && (before.kind ?? 'member') === (after.kind ?? 'member');
}
/**
 * Where the token may go. Identity's own origin and nothing else, unless a deployment names more.
 *
 * This is a rule of its own, not a consequence of the cookie: the refresh cookie is host-only on
 * identity and a unit that takes a bearer token never needs it. It is here, rather than at the
 * caller, because this is the object that holds the credential and so the only place that can
 * enforce it.
 *
 * An origin: scheme, host and port together. The rule this replaced dropped one label from
 * identity's hostname and trusted every sibling of what was left, which was a guess about who owns
 * a domain — with identity at api-core.dash.ps it trusted every other site on dash.ps, a box this
 * product shares with a dozen unrelated applications. A unit anywhere but identity's own origin is
 * a deployment fact, and a person states it one origin at a time in SHELL_TRUSTED_ORIGINS. There
 * is no wildcard, so that list can be long but never broad, and every refusal names the address.
 *
 * What this cannot say is who else answers on identity's origin. Where units are split by path
 * behind one host, the boundary is whoever controls routing there; that is an infrastructure
 * control and not something the shell can check.
 *
 * The port counts. On one host a different port is a different listener, so the local stack —
 * three units on 127.0.0.1 at three ports — names its own two the way any deployment does, and the
 * rule exercised there is the rule that runs in production.
 */
function destinationPolicy(identityUrl, extra) {
    // Only http(s) has an origin worth comparing: every other scheme serialises to "null", and two
    // opaque origins would then look equal to each other.
    const web = (u) => u.protocol === 'http:' || u.protocol === 'https:';
    let home;
    try {
        home = new URL(identityUrl);
    }
    catch {
        return () => false;
    }
    if (!web(home))
        return () => false;
    const allowed = new Set();
    for (const origin of extra) {
        try {
            const named = new URL(origin);
            if (web(named))
                allowed.add(named.origin);
        }
        catch { /* an unreadable origin trusts nothing */ }
    }
    return (url) => {
        let there;
        try {
            there = new URL(url);
        }
        catch {
            return false;
        }
        if (!web(there))
            return false;
        return there.origin === home.origin || allowed.has(there.origin);
    };
}
/** Identity's own address, exactly: the one the refresh cookie was set by, and the only one it may go to. */
function sameOrigin(url, identityUrl) {
    try {
        const there = new URL(url);
        const home = new URL(identityUrl);
        return (there.protocol === 'http:' || there.protocol === 'https:') && there.origin === home.origin
            && there.pathname.replace(/\/$/, '') === home.pathname.replace(/\/$/, '');
    }
    catch {
        return false;
    }
}
/** The payload half of a JWT, for display only. An unreadable token yields nothing, not a throw. */
function claimsOf(token) {
    const part = token.split('.')[1];
    if (!part)
        return {};
    try {
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const decode = globalThis.atob;
        // A browser has atob; a test runner on Node has it too since v16. Neither needs a polyfill,
        // and reaching for Buffer here would drag Node types into a package that runs in a page.
        if (!decode)
            return {};
        const json = decode(padded);
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {};
    }
}
/** The file's name from a Content-Disposition header: `filename*=UTF-8''…` first, then `filename="…"`. */
/**
 * What a unit that cannot answer says about why, beside its status: Nest's `{message}` (or `detail`),
 * cut to a sentence. «HTTP 503» alone tells a screen nothing; «HTTP 503: the model could not be
 * reached: Request timed out.» lets it say the model was slow. A body that is not JSON, or none,
 * leaves the status alone.
 */
async function reasonOf(res) {
    const status = `HTTP ${res.status}`;
    try {
        const body = JSON.parse(await res.text());
        const said = typeof body.message === 'string' ? body.message : typeof body.detail === 'string' ? body.detail : '';
        return said.trim() ? `${status}: ${said.trim().slice(0, 300)}` : status;
    }
    catch {
        return status;
    }
}
export function filenameOf(disposition) {
    if (!disposition)
        return null;
    const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].trim());
        }
        catch { /* fall through to the plain name */ }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/.exec(disposition);
    return plain?.[1]?.trim() || null;
}
function safeJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
function messageOf(body) {
    if (typeof body === 'string')
        return body.slice(0, 300);
    if (body && typeof body === 'object' && 'message' in body) {
        const m = body.message;
        if (typeof m === 'string')
            return m;
        if (m && typeof m === 'object' && 'message' in m && typeof m.message === 'string')
            return m.message;
    }
    return undefined;
}
/** The body field a refusal may be overruled with, when it names one: a plain key, nothing else. */
export function confirmOf(body) {
    if (!body || typeof body !== 'object')
        return undefined;
    const nested = body.message;
    const raw = body.confirm ?? (nested && typeof nested === 'object' ? nested.confirm : undefined);
    return typeof raw === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(raw) ? raw : undefined;
}
/** The first problem's key and params, for a failure that has room for one reason. */
function saidOf(problems) {
    const first = problems?.[0];
    return first?.key ? { key: first.key, ...(first.params ? { params: first.params } : {}) } : null;
}
function problemsOf(body) {
    if (!body || typeof body !== 'object')
        return undefined;
    const direct = body.problems;
    if (Array.isArray(direct))
        return direct;
    const nested = body.message;
    if (nested && typeof nested === 'object' && Array.isArray(nested.problems)) {
        return nested.problems;
    }
    return undefined;
}
/** AbortSignal.any where it exists; a listener pair where it does not. */
function anySignal(signals) {
    const any = AbortSignal.any;
    if (any)
        return any(signals);
    const controller = new AbortController();
    for (const s of signals) {
        if (s.aborted) {
            controller.abort(s.reason);
            break;
        }
        s.addEventListener('abort', () => controller.abort(s.reason), { once: true });
    }
    return controller.signal;
}
//# sourceMappingURL=session.js.map